import { Reference } from '@approvalmax/types';
import { arrayHelpers } from '@approvalmax/utils';
import uniqueId from 'lodash/uniqueId';
import { domain } from 'modules/data';
import { changeXeroManualJournalRequest } from 'pages/requestForm/actions';
import { XeroContext } from 'pages/requestForm/data/xero/XeroContext';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious } from 'react-use';

import {
    calculateLineTaxAmount,
    checkManualJournalLineIsEmpty,
    createNewManualJournalLine,
    expandManualJournalLine,
} from './XeroManualJournalRequestForm.helpers';
import { ExpandedXeroManualJournalLine, ManualJournalFields } from './XeroManualJournalRequestForm.types';

export const useFields = () => {
    const [fieldValues, setFieldValues] = useState<ManualJournalFields>({
        journalDate: '',
        narration: '',
        showOnCashBasisReports: true,
        defaultNarrationToDescription: true,
        lineAmountType: domain.LineAmountType.TaxExclusive,
    });

    const onFieldChange = useCallback(<K extends keyof ManualJournalFields>(key: K, value: ManualJournalFields[K]) => {
        setFieldValues((current) => ({
            ...current,
            [key]: value,
        }));
    }, []);

    const setFieldsValuesBulk = useCallback((values: Partial<ManualJournalFields>) => {
        setFieldValues((current) => ({
            ...current,
            ...values,
        }));
    }, []);

    return {
        fieldValues,
        onFieldChange,
        setFieldsValuesBulk,
    };
};

export const useManualJournalLines = (
    lineAmountType: domain.LineAmountType,
    trackingCategories: XeroContext['trackingCategories'],
    defaultNarrationToDescription: string | null
) => {
    const [manualJournalLines, setManualJournalLinesState] = useState<ExpandedXeroManualJournalLine[]>(() => [
        createNewManualJournalLine(defaultNarrationToDescription),
    ]);

    const dispatch = useDispatch();

    const prevNarration = usePrevious(defaultNarrationToDescription);

    useEffect(() => {
        if (lineAmountType === domain.LineAmountType.NoTax) {
            setManualJournalLinesState((current) => {
                return current.map((line) => {
                    return {
                        ...line,
                        tax: undefined,
                        taxAmount: undefined,
                    };
                });
            });
        } else {
            setManualJournalLinesState((current) => {
                return current.map((line) => {
                    let taxAmount = line.taxAmount;

                    if (typeof line.amount === 'number' && line.tax) {
                        taxAmount = calculateLineTaxAmount(line.amount, line.tax.rate, lineAmountType);
                    }

                    return {
                        ...line,
                        taxAmount,
                    };
                });
            });
        }
    }, [lineAmountType]);

    useEffect(() => {
        if (defaultNarrationToDescription === null) {
            return;
        }

        setManualJournalLinesState((current) => {
            return current.map((line) => {
                const newDescription =
                    line.description === prevNarration || !line.description
                        ? defaultNarrationToDescription
                        : line.description;

                return {
                    ...line,
                    description: newDescription || '',
                };
            });
        });
    }, [defaultNarrationToDescription, prevNarration]);

    const setManualJournalLines = useCallback((data: domain.XeroManualJournalLine[]) => {
        if (data.length === 0) {
            setManualJournalLinesState([createNewManualJournalLine()]);
        } else {
            setManualJournalLinesState(data.map((line) => expandManualJournalLine(line)));
        }
    }, []);

    const onManualJournalLineAdd = useCallback(() => {
        setManualJournalLinesState((current) => [
            ...current,
            createNewManualJournalLine(defaultNarrationToDescription),
        ]);

        dispatch(changeXeroManualJournalRequest());
    }, [dispatch, defaultNarrationToDescription]);

    const onManualJournalLinesReorder = useCallback(
        (oldIndex: number, newIndex: number) => {
            setManualJournalLinesState((current) => {
                return arrayHelpers.arrayMove(current, oldIndex, newIndex);
            });

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch]
    );

    const onManualJournalLineRemove = useCallback(
        (id: string) => {
            setManualJournalLinesState((current) => {
                const lines = current.filter((item) => item.id !== id);

                if (lines.length === 0) {
                    return [createNewManualJournalLine(defaultNarrationToDescription)];
                }

                return lines;
            });

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch, defaultNarrationToDescription]
    );

    const onManualJournalLineClone = useCallback(
        (id: string) => {
            setManualJournalLinesState((current) => {
                const targetLine = current.find((line) => line.id === id);

                if (targetLine) {
                    return [
                        ...current,
                        {
                            ...targetLine,
                            id: uniqueId('xeroManualJournalLine-'),
                        },
                    ];
                }

                return current;
            });

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch]
    );

    const onManualJournalLineFieldUpdate = useCallback(
        <K extends keyof ExpandedXeroManualJournalLine>(
            id: string,
            key: K,
            value: ExpandedXeroManualJournalLine[K]
        ) => {
            setManualJournalLinesState((current) =>
                current.map((line) => {
                    if (line.id === id) {
                        const newLine = {
                            ...line,
                            [key]: value,
                        };

                        if (key === 'amount' || key === 'tax') {
                            if (
                                lineAmountType !== domain.LineAmountType.NoTax &&
                                typeof newLine.amount === 'number' &&
                                newLine.tax
                            ) {
                                const taxableAmount =
                                    (newLine.amount * 100) /
                                    (100 +
                                        (lineAmountType === domain.LineAmountType.TaxExclusive ? 0 : newLine.tax.rate));

                                const taxAmount = Math.round(taxableAmount * newLine.tax.rate) / 100;

                                newLine.taxAmount = taxAmount;
                            }
                        }

                        newLine.isEmpty = checkManualJournalLineIsEmpty(newLine);

                        return newLine;
                    }

                    return line;
                })
            );

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch, lineAmountType]
    );

    const onManualJournalLineTrackingFieldUpdate = useCallback(
        (id: string, trackingCategoryId: string, value: Reference | null) => {
            const trackingCategory = trackingCategories.find((item) => item.category.id === trackingCategoryId);

            setManualJournalLinesState((current) =>
                current.map((line) => {
                    if (line.id === id && trackingCategory) {
                        const newLine: ExpandedXeroManualJournalLine = {
                            ...line,
                        };

                        if (value === null) {
                            newLine.tracking = newLine.tracking.filter(
                                (item) => item.category.id !== trackingCategoryId
                            );

                            newLine.isEmpty = checkManualJournalLineIsEmpty(newLine);

                            return newLine;
                        }

                        const hasTrackingValue = line.tracking.find((item) => item.category.id === trackingCategoryId);

                        if (hasTrackingValue) {
                            newLine.tracking = newLine.tracking.map((item) => {
                                if (item.category.id === trackingCategoryId) {
                                    return {
                                        ...item,
                                        value,
                                    };
                                }

                                return item;
                            });
                        } else {
                            newLine.tracking = [
                                ...newLine.tracking,
                                {
                                    category: trackingCategory.category,
                                    value,
                                },
                            ];
                        }

                        newLine.isEmpty = checkManualJournalLineIsEmpty(newLine);

                        return newLine;
                    }

                    return line;
                })
            );

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch, trackingCategories]
    );

    const updateManualJournalLine = useCallback(
        (id: string, data: Partial<ExpandedXeroManualJournalLine>) => {
            setManualJournalLinesState((current) =>
                current.map((line) => {
                    if (line.id === id) {
                        const updatedLine = {
                            ...line,
                            ...data,
                        };

                        updatedLine.isEmpty = checkManualJournalLineIsEmpty(updatedLine);

                        return updatedLine;
                    }

                    return line;
                })
            );

            dispatch(changeXeroManualJournalRequest());
        },
        [dispatch]
    );

    return {
        manualJournalLines,
        setManualJournalLines,
        onManualJournalLineAdd,
        onManualJournalLinesReorder,
        onManualJournalLineRemove,
        onManualJournalLineClone,
        onManualJournalLineFieldUpdate,
        onManualJournalLineTrackingFieldUpdate,
        updateManualJournalLine,
    };
};
